import type { Tenant } from '../../lib/api';
import styles from './BrandConfigCard.module.css';
import { TenantLogo } from './TenantLogo';

const EMPTY_VALUE = 'Not set';

interface BrandConfigCardProps {
  branding: Tenant['branding'];
  tenantName: string;
}

export function BrandConfigCard({ branding, tenantName }: BrandConfigCardProps) {
  const logoUrl = branding?.logoUrl?.trim() || null;
  const logoBase64 = branding?.logoBase64?.trim() || null;
  const logoIsConfigured = Boolean(logoUrl || logoBase64);
  const logoSourceLabel = logoUrl ?? (logoBase64 ? 'Base64 image configured' : EMPTY_VALUE);
  const logoTextClassName = logoIsConfigured ? styles.value : styles.mutedValue;
  const primaryColor = branding?.primaryColor?.trim() || null;
  const brandName = branding?.brandName?.trim() || null;
  const primaryEmail = branding?.primaryEmail?.trim() || null;
  const brandNameText = brandName ?? EMPTY_VALUE;
  const brandNameTextClassName = brandName ? styles.value : styles.mutedValue;

  return (
    <section className={styles.section} aria-labelledby="tenant-brand-config-heading">
      <h4 id="tenant-brand-config-heading" className={styles.title}>
        Branding
      </h4>
      <dl className={styles.brandFields}>
        <div className={styles.wideItem}>
          <dt className={styles.label}>Logo</dt>
          <dd className={styles.logoValue}>
            <TenantLogo branding={branding} tenantName={tenantName} size="lg" tone="neutral" />
            <span className={styles.logoMeta}>
              {logoUrl ? (
                <a
                  href={logoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.logoSource}>
                  {logoSourceLabel}
                </a>
              ) : (
                <span className={logoTextClassName}>{logoSourceLabel}</span>
              )}
            </span>
          </dd>
        </div>

        <div className={styles.item}>
          <dt className={styles.label}>Primary Color</dt>
          {primaryColor ? (
            <dd className={styles.colorValue}>
              <span
                aria-label={`Primary color ${primaryColor}`}
                className={styles.colorSwatch}
                style={{ backgroundColor: primaryColor }}
              />
              <span className={styles.colorCode}>{primaryColor}</span>
            </dd>
          ) : (
            <dd className={styles.mutedValue}>{EMPTY_VALUE}</dd>
          )}
        </div>

        <div className={styles.item}>
          <dt className={styles.label}>Brand Name</dt>
          <dd className={brandNameTextClassName}>{brandNameText}</dd>
        </div>

        <div className={styles.item}>
          <dt className={styles.label}>Primary Email</dt>
          {primaryEmail ? (
            <dd className={styles.value}>
              <a href={`mailto:${primaryEmail}`} className={styles.logoSource}>
                {primaryEmail}
              </a>
            </dd>
          ) : (
            <dd className={styles.mutedValue}>{EMPTY_VALUE}</dd>
          )}
        </div>
      </dl>
    </section>
  );
}
